import { enqueueSnackbar } from 'notistack';
import { ERROR_401 } from 'common/config/api';
import { SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_MESSAGE_TIMEOUT, SIGN_IN_URL } from 'common/constants/login';
import AuthService from './auth';
import BaseService from './base';

const refreshAccessToken = async (): Promise<void> => {
  const authService = AuthService.getInstance();
  const refreshToken = localStorage.getItem('refresh');

  const payload = {
    refresh: refreshToken,
  };
  if (refreshToken) {
    try {
      const newAccessToken = await authService.refreshAccessToken(JSON.stringify(payload));

      if (newAccessToken) {
        localStorage.setItem('access', newAccessToken.access);
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error && error.status === Number(ERROR_401)) {
        localStorage.removeItem('refresh');
        localStorage.removeItem('access');
        localStorage.removeItem('isOtpVerified');
        enqueueSnackbar(SESSION_EXPIRED_MESSAGE, {
          variant: 'info',
        });
        // Prevent any further API requests
        setTimeout(() => {
          window.location.href = SIGN_IN_URL;
        }, SESSION_EXPIRED_MESSAGE_TIMEOUT);
      }
    }
  }
};

const refreshPassThrough = async (next: () => Promise<unknown>) => {
  let response;
  try {
    response = await next();
  } catch (e: any) {
    if (e.status === 401) {
      try {
        await refreshAccessToken();
        response = await next();
      } catch (err) {
        return Promise.reject(e);
      }
      return Promise.resolve(response);
    }
    return Promise.reject(e);
  }
  return Promise.resolve(response);
};

class BaseServiceRetryWrapper extends BaseService {
  async get(url: string, query?: unknown, moduleView?: string, isList?: boolean, headers?: Headers): Promise<unknown> {
    return refreshPassThrough(() => super.get(url, query, moduleView, isList, headers));
  }

  async getFile(
    url: string,
    data?: unknown,
    moduleView?: string,
    isList?: boolean,
    headers?: Headers
  ): Promise<unknown> {
    return refreshPassThrough(() => super.getFile(url, data, moduleView, isList, headers));
  }

  async put(url: string, data?: unknown, moduleView?: string, isList?: boolean, headers?: Headers): Promise<unknown> {
    return refreshPassThrough(() => super.put(url, data, moduleView, isList, headers));
  }

  async post(url: string, data?: unknown, moduleView?: string, isList?: boolean, headers?: Headers): Promise<unknown> {
    return refreshPassThrough(() => super.post(url, data, moduleView, isList, headers));
  }

  async delete(url: string, moduleView?: string, isList?: boolean, headers?: Headers): Promise<unknown> {
    return refreshPassThrough(() => super.delete(url, moduleView, isList, headers));
  }

  async patch(url: string, data?: unknown, moduleView?: string, isList?: boolean, headers?: Headers): Promise<unknown> {
    return refreshPassThrough(() => super.patch(url, data, moduleView, isList, headers));
  }
}

export default BaseServiceRetryWrapper;
